/**
 * Rate-based attitude control (AD-007c): stick and rudder command body-axis
 * angular rates through per-aircraft response gains, and the quaternion
 * integrates those rates. Angle of attack is then whatever the difference
 * between where the nose points and where the aircraft is going works out to,
 * which is what makes lift curve the flight path back toward the nose without
 * any artificial damping term.
 *
 * Field provenance (docs/engineering/flight-tunables.md §2):
 * - 42–43 control response — REPORTED as "control response", derived by feel.
 *   Its mapping onto pitch/roll rate ceilings is INFERRED: a gain relative to
 *   the 400 class, not an absolute rate.
 * - 54–55 rudder yaw rate — REPORTED, and MEASURED-DOSBOX at ÷41 deg/s
 *   (AD-007j: a held rudder yaws the Camel at a dead-steady 4.40 °/s, and its
 *   field 54 is 180). The ÷30 the turn fields use was never confirmed here.
 * - 26–27 rudder authority under load — REPORTED, fleet-constant at 120 with
 *   the Bristol F.2B at 140.
 * - 66/68 rotary torque and 56 the rotary coupling term — REPORTED / INFERRED.
 *
 * `commandedRates`/`advanceRates` cover the three body axes (roll, pitch,
 * yaw) that decoupled control-rate calibration confirmed exact
 * (flight-tunables.md §8.1). `producedHeadingRateRadPerSec` — the AD-007h turn
 * mechanism, a fourth and separate rate that curves the flight path and carries
 * the nose with it — lives in `produced-turn.ts`, split out when AD-007l's
 * fitted bank shape pushed this file past the 400-line gate.
 * `slipRelaxationRates` is a fifth (AD-007i, amended by AD-007j) and
 * `rotaryPrecessionYawRadPerSec` a sixth (AD-007m); both deliberately keep out
 * of `commandedRates` too — see their own notes.
 */

import type { BodyRates } from "./math3d.ts";
import type { FlightInputs } from "./inputs.ts";
import type { SubsystemFlags } from "./tiers.ts";
import type { Tunables } from "./tunables.ts";
import { clamp } from "./units.ts";

export interface ControlSpec {
  /** Raw `.DAT` control response, offset 42. */
  readonly responseRaw: number;
  readonly rollRateMaxRadPerSec: number;
  readonly pitchRateMaxRadPerSec: number;
  /** From offset 54 via the ÷41 deg/s unit (`rudderDegPerSecondPerRateCount`, AD-007j). */
  readonly yawRateMaxRadPerSec: number;
  /** Raw `.DAT` offset 26 — rudder authority retained under load. */
  readonly rudderAuthorityRaw: number;
  readonly rateLagSeconds: number;
}

export interface TurnSpec {
  /** Raw `.DAT` offsets 116 and 118. Equal on 31 of 33 aircraft; Camel 930/700, Snipe 840/780. */
  readonly rightRaw: number;
  readonly leftRaw: number;
  readonly rightRadPerSec: number;
  readonly leftRadPerSec: number;
}

export interface TorqueSpec {
  /** From aircraft.md §9, not the file — there is no rotary flag in the data. */
  readonly rotary: boolean;
  /** Offset 66: rolling torque, negative in the file for a right-rolling engine. */
  readonly rollRaw: number;
  /** Offset 68: the paired term, −0.5 × field 66 (−0.4 × on the Sopwith Triplane). */
  readonly gyroRaw: number;
  /** Offset 56: the rotary-only negative coupling term, read here as slipstream/P-factor yaw. INFERRED. */
  readonly couplingRaw: number;
}

export interface ControlDemand {
  readonly control: ControlSpec;
  readonly torque: TorqueSpec;
  readonly flags: SubsystemFlags;
  readonly tunables: Tunables;
  readonly inputs: FlightInputs;
  readonly rates: BodyRates;
  readonly betaRad: number;
  /** True airspeed, for the `airspeedAuthority` fade. */
  readonly airspeedMps: number;
  /** Delivered power as a fraction of sea-level maximum — rotary effects scale with it. */
  readonly powerFraction: number;
  /** 0..1 health of the control surfaces. */
  readonly controlHealth: number;
  /** Rate biases contributed by other subsystems (spiral, spin, structural failure). */
  readonly extraRates: BodyRates;
}

/** Control authority left after damage, floored so a crippled aircraft is still steerable. */
export function controlAuthority(controlHealth: number, tunables: Tunables): number {
  return clamp(controlHealth, tunables.controlDamageFloor, 1);
}

/**
 * Control effectiveness from airflow: a control surface deflects the aeroplane
 * through dynamic pressure, so the *rate* a given stick position commands is
 * not the same at 20 mph as at 95 mph. Full authority at and above
 * `controlFullAuthoritySpeedMps`, falling as `(V/Vfull)²` below it — the `q`
 * ratio, which is the one shape this needs no extra constant to justify.
 *
 * The original's own rate-command structure is unchanged by this (a rate is
 * still commanded, held, and never damped toward level — flight-tunables.md
 * §8.1 finding 7); what changes is the size of the rate available deep below
 * cruise. The session bounds it from both sides: every rate it timed was flown
 * at or above ~95 mph and must be untouched, the Camel still sustains 1-g
 * climbing flight at 42.5 mph (§4.1) so authority is set to be exactly full
 * there, and a full-back zoom that decays past ~25 mph stops obeying the stick
 * altogether — the nose sags *against* full back pressure (§4.5). Without this
 * term the port holds 22 °/s of commanded pitch at 13 mph, drives a full-back
 * zoom vertical and over the top, and departs from an attitude the original
 * never reaches.
 */
export function airspeedAuthority(airspeedMps: number, tunables: Tunables): number {
  const ratio = clamp(airspeedMps / Math.max(tunables.controlFullAuthoritySpeedMps, 1e-3), 0, 1);
  return ratio * ratio;
}

/**
 * The rotary engine's *steady* biases, Expert tier only and rotary-only — the
 * shape AD-007b's subsystem table calls for, kept parameterized because a third
 * calibration aircraft could revive it.
 *
 * **Both surviving gains are MEASURED-DOSBOX zero**, so at the shipped tunables
 * this contributes nothing: forty seconds hands-off in the original's Camel at
 * full throttle and Expert held bank at exactly 0.00° and heading to the last
 * stored digit, and the model has no auto-levelling to hide a bias with.
 *
 * The gyroscopic cross-coupling that used to live here has **left this
 * function**, in both of its directions (AD-007m). Rudder→pitch is a hard
 * structural zero — measured at exactly 0.00° of pitch through every rudder hold
 * on two aircraft, twice, so there is nothing left to parameterize. Pitch→yaw is
 * real, is 0.36·q on the Camel, and is now `rotaryPrecessionYawRadPerSec` below,
 * applied kinematically to the orientation rather than as a commanded rate; see
 * that function for why the two directions cannot share a constant and why this
 * one is not a command.
 *
 * Signs, for whoever revives the roll bias: the file stores field 66 negative
 * for the right-rolling engines, so it is negated onto our roll-right-positive
 * axis.
 */
function rotaryBias(demand: ControlDemand): BodyRates {
  const { torque, tunables } = demand;
  if (!demand.flags.rotaryTorque || !torque.rotary) return { roll: 0, pitch: 0, yaw: 0 };
  const power = clamp(demand.powerFraction, 0, 1);
  return {
    roll: -torque.rollRaw * tunables.rotaryTorqueRollGain * power,
    pitch: 0,
    yaw: torque.couplingRaw * tunables.rotarySlipstreamGain * power,
  };
}

export interface PrecessionDemand {
  readonly torque: TorqueSpec;
  readonly flags: SubsystemFlags;
  readonly tunables: Tunables;
  /** Body pitch rate, rad/s, nose-up-positive — the rate the precession is proportional to. */
  readonly pitchRateRadPerSec: number;
}

/**
 * AD-007m: gyroscopic precession, the one direction that is measurably there.
 *
 * A rotary's whole engine is the flywheel, so pitching the airframe torques its
 * angular momentum and the aeroplane answers in yaw. flight-tunables.md §8.5.4
 * measures it: on the Camel the body yaw rate under held elevator is
 * **r = 0.36·q** with no constant part, and it *reverses* with the stick — full
 * back gives +0.35/+0.36 and full forward +0.39/+0.36 — which is precession's
 * signature and not a trim bias. The S.E.5a, a stationary V-8, shows none of it.
 *
 * Three things about the shape, each of them a measurement rather than a choice:
 *
 * - **It is one-directional.** The reverse coupling, rudder→pitch, is exactly
 *   0.00° on both aircraft through every rudder hold in two sessions. The single
 *   `rotaryGyroGain` this replaces drove both directions off one constant and so
 *   could not be right in both; it was pinned at zero by the rudder measurement,
 *   which silently zeroed the direction that is not zero.
 * - **It is gated to the rotaries with a torque pair.** Eight airframes carry a
 *   non-zero field 68; the other five "rotaries" and every inline are zero and
 *   get nothing. The S.E.5a contrast is the gate's evidence. (§8.5.4 also
 *   records what the S.E.5a *does* do — a constant ≈+2.2 °/s right-yaw whenever
 *   the elevator moves, same sign both ways, so not precession and probably
 *   slipstream. It is not modelled; it is named there so it is a gap and not a
 *   surprise.)
 * - **It rotates the orientation only, and not through `commandedRates`.** Same
 *   three reasons AD-007i gave: the three command axes are calibrated exact and
 *   must not acquire a hidden aero term, a coupling must not inherit the
 *   actuator's first-order lag, and `sim.rates` feeds the spin/spiral logic,
 *   which should keep reading what the *pilot* commands. `step.ts` applies it in
 *   the same kinematic slot as the α-relaxation and the path alignment.
 *
 * **The one deviation from the AD's letter, and it is load-bearing: `step.ts`
 * feeds this to `applyWorldYaw` — a bank-preserving heading rotation — rather
 * than integrating it as a body-axis rate.** The body-axis form reproduces
 * r/q = 0.36 at any pitch attitude by construction and was tried first; it is
 * wrong, because it carries the rigid-body Euler coupling `φ̇ = r·tan θ` with it,
 * and that rolls the Camel to 7° of bank two seconds into a full-back pull and
 * 74° by four, then departs §4.5's zoom into a spin the original does not fly.
 * The original holds 0.6° through the same manoeuvre and reads bank at exactly
 * 0.00° through every hold in §8.5.3 and §8.5.4; its attitude is three decoupled
 * Euler axes (§8.1 finding 7), so a heading increment is what it can express.
 * The price is that this port's r/q, computed the trace's own way as
 * `ψ̇·cos θ`, is 0.360 while the nose is near the horizon and 0.314 averaged over
 * a 2.4 s window in which the pitch attitude climbs to 45°. Both are pinned.
 *
 * Zero when the stick is still (q = 0), so hands-off flight is untouched.
 */
export function rotaryPrecessionYawRadPerSec(demand: PrecessionDemand): number {
  const { torque, tunables } = demand;
  if (!demand.flags.rotaryTorque || !torque.rotary || torque.gyroRaw === 0) return 0;
  const gain =
    tunables.rotaryPrecessionYawPerPitchRate *
    (torque.gyroRaw / Math.max(tunables.rotaryPrecessionReferenceTorque, 1e-6));
  return gain * demand.pitchRateRadPerSec;
}

/**
 * Target body rates for this tick, before the first-order actuator lag.
 *
 * **Two of the three axes fade with airspeed and the third does not** — AD-007l,
 * on flight-tunables.md §8.5.3. Full rudder held at 88.2, 71.1 and 61.1 mph
 * yaws the original's Camel at 4.49 °/s at all three, to three significant
 * figures, where `airspeedAuthority` is well under 1 at the bottom of that
 * range; the rudder is a pure rate command with no airspeed authority at all.
 * Pitch and roll keep the fade: every §8.1 rate that pins them was flown at
 * ~95 mph where the fade is exactly 1, and §4.5's zoom — the nose sagging
 * against full back stick at ~25 mph — is the fade being *measured* on the pitch
 * axis. Nothing in session 3 touches either.
 *
 * Damage authority (`controlAuthority`) still applies to all three: a shot-away
 * rudder is a different quantity from a rudder in slow air.
 */
export function commandedRates(demand: ControlDemand): BodyRates {
  const { control, inputs, tunables } = demand;
  const health = controlAuthority(demand.controlHealth, tunables);
  const authority = health * airspeedAuthority(demand.airspeedMps, tunables);
  const rudderScale = control.rudderAuthorityRaw / tunables.rudderAuthorityReference;
  const bias = rotaryBias(demand);

  const assistYaw = demand.flags.bankToTurnAssist
    ? demand.betaRad * tunables.noviceAutoRudderGain
    : 0;

  // AD-007n: the fleet rolls faster left than right. Two identical aileron ramps
  // in opposite directions put the Camel's plateau at 50–53 °/s left against
  // 39.5 °/s right, which also means §4.3's long-quoted 49.4 °/s — the number
  // `rollRateBaseDegPerSec` is calibrated to — is the *left* figure. So the
  // ceiling stays what it was and the right-hand roll is divided down.
  const rollInput = clamp(inputs.roll, -1, 1);
  const rollCeiling =
    rollInput > 0
      ? control.rollRateMaxRadPerSec / Math.max(tunables.rollRightPenalty, 1e-3)
      : control.rollRateMaxRadPerSec;

  return {
    roll: rollInput * rollCeiling * authority + bias.roll + demand.extraRates.roll,
    pitch:
      clamp(inputs.pitch, -1, 1) * control.pitchRateMaxRadPerSec * authority +
      bias.pitch +
      demand.extraRates.pitch,
    yaw:
      clamp(inputs.yaw, -1, 1) * control.yawRateMaxRadPerSec * health * rudderScale +
      assistYaw +
      bias.yaw +
      demand.extraRates.yaw,
  };
}

export interface SlipRelaxationDemand {
  readonly tunables: Tunables;
  /** Angle of attack, positive nose above the flight path. */
  readonly alphaRad: number;
  /** Sideslip, positive when the flight path runs to the right of the nose. */
  readonly betaRad: number;
  readonly airspeedMps: number;
  /**
   * The angle of attack the pilot's own load-factor command implies at this
   * moment — `elevator-path.ts`'s `alphaReferenceRad` (AD-007n change 2).
   * Defaults to the current α, which makes the pitch half exactly zero: the
   * shape every stick-centred caller wants, and the one that keeps trimmed
   * hands-off flight bit-constant.
   */
  readonly referenceAlphaRad?: number;
  /**
   * `aero.ts`'s `postStallFactor` at the current α — the shared collapse curve
   * AD-007h already applies to the produced turn. A departed wing does not
   * align the nose to the path either, and gating on it is what leaves the spin
   * model intact under an ungated relaxation. Defaults to 1.
   */
  readonly stallFactor?: number;
}

export interface SlipRelaxationRates {
  /**
   * AD-007j's path alignment: a **yaw rate for the velocity vector**, rad/s,
   * right-positive, about the aircraft's own yaw axis. Not a body rate and not
   * an attitude rate — `step.ts` feeds it to `yawAboutBodyUp` beside AD-007h's
   * produced turn, and the orientation never sees it.
   */
  readonly pathAlignRadPerSec: number;
  /** α-relaxation pitch rate, rad/s, nose-up-positive (so a positive α gives a negative rate). */
  readonly pitchRadPerSec: number;
}

/**
 * What sideslip and a faded-authority angle of attack do — and the two halves
 * act on *different* things, which is the whole point of AD-007j.
 *
 * **Yaw — the path aligns to the nose, MEASURED-DOSBOX.** `−β ·
 * pathAlignRatePerRad`, applied to the velocity alone, about the aircraft's own
 * yaw axis (`yawAboutBodyUp`, which is where the choice of axis is argued).
 * β > 0 is the path running right of the nose (aero.ts's convention), so the
 * path has to swing *left* — a negative yaw — to close it, which is the leading
 * minus. AD-007i had this backwards: it yawed the nose onto the path with a
 * weathercock, and its own two DOSBox experiments then refuted that outright.
 * Hold full rudder in the original and the nose goes round at a dead-steady
 * 4.4 °/s with the path following it at only ≈2° of slip; centre the rudder and
 * the attitude freezes to the last stored digit while the path swings onto it in
 * about a second. There is no weathercock term any more, deliberately — see
 * `pathAlignRatePerRad`.
 *
 * **There is no speed scaling, and the absence is measured.** AD-007j shipped a
 * `(v/43.7)` factor on a physical argument with one traced speed behind it;
 * flight-tunables.md §8.5.3 traced two more and the steady slip goes the wrong
 * way — 0.91× at 61 mph against the 1.45× linear-in-v predicts. AD-007l deleted
 * the factor and `pathAlignReferenceSpeedMps` with it, and refit the gain
 * against the same three traces.
 *
 * **Pitch — the attitude follows the flight path, at every speed (AD-007n
 * change 2).** `−(α − α_ref) · alphaRelaxRatePerRad · lowSpeedGain`, applied to
 * the orientation alone. AD-007i's `(1 − airspeedAuthority)` gate is **gone**:
 * it made the term identically zero above 42.5 mph, which is every speed the
 * game is played at, and `docs/engineering/flight-feel-findings.md` §2 measures
 * the original holding α between −1.4° and +5° through three manoeuvres at 50 to
 * 101 mph that have nothing else in common — wings level to 68° of bank,
 * climbing at 1,600 fpm and diving at 2,500. flight-tunables.md §8.5.1 finding 4
 * said the same thing more weakly at cruise ("the flight path angle sits within
 * 0.5–2° of the pitch attitude") and §4.2's dive carried a steady 2.6°. The gate
 * was honest when AD-007i chose it — it had only §4.5's low-speed nose-sag to go
 * on and wanted the term nowhere near §8.1's calibrated pitch rates — and it is
 * what let α reach 67° in a 26°-banked pull.
 *
 * What replaces the gate is the *reference*: `elevator-path.ts`'s
 * `alphaReferenceRad`, the α the stick's own load-factor command implies. See
 * that function for why an ungated relaxation needs one — relaxing toward zero
 * would sag the nose out of trimmed flight, where the port's Camel needs
 * α = 3.8° for its 1 g — and for why the reference is exactly the current α with
 * the stick centred, which is what keeps trim bit-constant.
 *
 * The old gate's *shape* survives, with its sign the other way up:
 * `alphaRelaxLowSpeedFloor` fades the relaxation toward a floor as
 * `airspeedAuthority` dies, on the same argument that fades the pitch and roll
 * commands — a wing in slow air does not weathercock the nose onto the path any
 * more than an elevator in slow air deflects it. That fade is what keeps a stall
 * *reachable*: with the relaxation at full strength everywhere, α is pinned near
 * the reference at every speed and the wing can never break, which would take
 * §4.5's own "asymmetric entry, rudder held at the stall" out of the game along
 * with the departures this AD exists to remove. Below the knee the nose lags the
 * falling flight path, α grows, and a slow enough zoom with rudder in it still
 * spins the types the data says spin.
 *
 * Neither rate goes through `commandedRates`/`advanceRates`: the three command
 * axes are calibrated exact and must stay free of hidden aero terms, a
 * restoring rate must not inherit the actuator's first-order lag, and
 * `sim.rates` feeds the spin/spiral logic, which should keep reading what the
 * *pilot* commands. `step.ts` applies these kinematically instead.
 *
 * Both are exactly zero at zero angle — and the pitch half is exactly zero
 * whenever α is already the α the stick is asking for, which with the stick
 * centred is always — so trimmed hands-off flight stays bit-constant.
 * `advanceEulerAttitude` and `yawAboutWorldUp` both short-circuit on a zero
 * rate.
 */
export function slipRelaxationRates(demand: SlipRelaxationDemand): SlipRelaxationRates {
  const { tunables } = demand;
  const reference = demand.referenceAlphaRad ?? demand.alphaRad;
  const lowSpeedGain =
    tunables.alphaRelaxLowSpeedFloor +
    (1 - tunables.alphaRelaxLowSpeedFloor) * airspeedAuthority(demand.airspeedMps, tunables);
  return {
    // The alignment fades with the same shape and for the same reason (AD-007n):
    // a wing and fin in slow air do not swing the flight path onto the nose. All
    // three of §8.5.3's traces were flown at 61–88 mph where the factor is
    // exactly 1, so none of them moves. What it buys is the other half of a
    // reachable spin: with the pedal held below the stall the nose goes on
    // yawing at its measured 4.4 °/s and the path stops following, so the
    // sideslip a deliberate provocation needs actually builds — where a
    // full-authority alignment pins it at 2° and no aeroplane can be made to
    // depart on purpose.
    pathAlignRadPerSec: -demand.betaRad * tunables.pathAlignRatePerRad * lowSpeedGain,
    pitchRadPerSec:
      -(demand.alphaRad - reference) *
      tunables.alphaRelaxRatePerRad *
      lowSpeedGain *
      (demand.stallFactor ?? 1),
  };
}

/** First-order lag from the current body rates toward the commanded ones. */
export function advanceRates(current: BodyRates, target: BodyRates, blend: number): BodyRates {
  const factor = clamp(blend, 0, 1);
  return {
    roll: current.roll + (target.roll - current.roll) * factor,
    pitch: current.pitch + (target.pitch - current.pitch) * factor,
    yaw: current.yaw + (target.yaw - current.yaw) * factor,
  };
}
